/**
 * Utilitaire de retry avec backoff exponentiel
 * Permet de réessayer automatiquement les opérations échouées
 */

export interface RetryOptions {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: any, attempt: number) => boolean;
  onRetry?: (error: any, attempt: number, delayMs: number) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  shouldRetry: (error: any) => {
    // Retry sur erreurs réseau et erreurs 5xx
    if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      return true;
    }
    if (error?.status >= 500 && error?.status < 600) {
      return true;
    }
    // Retry sur timeout
    if (error?.code === 'ETIMEDOUT' || error?.message?.includes('timeout')) {
      return true;
    }
    return false;
  },
  onRetry: (error: any, attempt: number, delayMs: number) => {
    console.warn(`🔄 Retry attempt ${attempt} after ${delayMs}ms:`, error?.message || error);
  }
};

/**
 * Fonction de délai (promesse qui se résout après X ms)
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calcule le délai avec backoff exponentiel + jitter
 */
function calculateDelay(
  attempt: number,
  initialDelay: number,
  maxDelay: number,
  multiplier: number
): number {
  // Backoff exponentiel: initialDelay * (multiplier ^ attempt)
  const exponentialDelay = initialDelay * Math.pow(multiplier, attempt - 1);
  
  // Limiter au délai max
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  
  // Ajouter jitter (±25%) pour éviter les thundering herds
  const jitter = cappedDelay * 0.25 * (Math.random() * 2 - 1);
  
  return Math.floor(cappedDelay + jitter);
}

/**
 * Wrapper de retry pour les fonctions asynchrones
 * 
 * @example
 * ```typescript
 * const data = await withRetry(
 *   async () => {
 *     const response = await fetch('/api/data');
 *     if (!response.ok) throw new Error('Request failed');
 *     return response.json();
 *   },
 *   { maxAttempts: 3 }
 * );
 * ```
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  let lastError: any;
  
  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      // Tenter l'opération
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Si c'est la dernière tentative, throw directement
      if (attempt === opts.maxAttempts) {
        console.error(`❌ All ${opts.maxAttempts} retry attempts failed:`, error);
        throw error;
      }
      
      // Vérifier si on doit retry
      if (!opts.shouldRetry(error, attempt)) {
        console.warn(`⚠️ Error not retryable, aborting:`, error?.message || error);
        throw error;
      }
      
      // Calculer le délai
      const delayMs = calculateDelay(
        attempt,
        opts.initialDelayMs,
        opts.maxDelayMs,
        opts.backoffMultiplier
      );
      
      // Notifier avant le retry
      opts.onRetry(error, attempt, delayMs);
      
      // Attendre avant de réessayer
      await delay(delayMs);
    }
  }
  
  // Ne devrait jamais arriver ici, mais TypeScript veut un return
  throw lastError;
}

/**
 * Wrapper spécifique pour les requêtes fetch avec retry
 */
export async function fetchWithRetry(
  url: string,
  init?: RequestInit,
  options?: RetryOptions
): Promise<Response> {
  return withRetry(
    async () => {
      const response = await fetch(url, init);
      
      // Si erreur 5xx, throw pour déclencher le retry
      if (response.status >= 500 && response.status < 600) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      
      return response;
    },
    {
      ...options,
      shouldRetry: (error, attempt) => {
        // Retry sur erreurs réseau
        if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
          return true;
        }
        // Retry sur erreurs 5xx
        if (error?.message?.includes('Server error:')) {
          return true;
        }
        // Custom shouldRetry si fourni
        if (options?.shouldRetry) {
          return options.shouldRetry(error, attempt);
        }
        return false;
      }
    }
  );
}

/**
 * Wrapper pour les requêtes Supabase avec retry
 */
export async function supabaseWithRetry<T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  options?: RetryOptions
): Promise<{ data: T | null; error: any }> {
  return withRetry(
    async () => {
      const result = await operation();
      
      // Si erreur Supabase, throw pour déclencher retry
      if (result.error) {
        throw result.error;
      }
      
      return result;
    },
    {
      ...options,
      shouldRetry: (error, attempt) => {
        // Retry sur erreurs réseau/timeout
        if (error?.message?.includes('FetchError') || 
            error?.message?.includes('NetworkError') ||
            error?.message?.includes('timeout')) {
          return true;
        }
        
        // Retry sur rate limiting (429)
        if (error?.status === 429) {
          return true;
        }
        
        // Retry sur erreurs serveur Supabase (5xx)
        if (error?.status >= 500 && error?.status < 600) {
          return true;
        }
        
        // Custom shouldRetry si fourni
        if (options?.shouldRetry) {
          return options.shouldRetry(error, attempt);
        }
        
        return false;
      }
    }
  );
}
